import React, {
  PropsWithChildren,
  ReactNode,
  useRef,
  useCallback,
  MouseEvent,
} from 'react';

import clsx from 'clsx';

import { useOutsideClickDetection } from '@hooks/use-outside-click-detection';

import { IconType, Icon } from '@atoms';

import classes from './filter-container.module.scss';

type Props = PropsWithChildren<{
  className?: string;
  prefixIcon?: IconType;
  noPadding?: boolean;
  dropdown?: {
    fullWidth?: boolean;
    component?: ReactNode;
    isOpen?: boolean;
    arrowSuffix?: boolean;
    allowOverflow?: boolean;
  };
  notification?: boolean;
  onClear?: () => void;
  onClickOutside?: () => void;
  onClick?: (e: MouseEvent<HTMLDivElement>) => void;
}>;

export const FilterContainer = ({
  className,
  prefixIcon,
  noPadding,
  dropdown = {},
  notification,
  onClear,
  onClickOutside = () => {},
  onClick = () => {},
  children,
}: Props) => {
  // refs
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // click outside hook
  useOutsideClickDetection(containerRef, onClickOutside);

  // on click
  const onContainerClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      // dismiss click into dropdown
      if (
        dropdownRef.current &&
        dropdownRef.current.contains(e.target as Node)
      ) {
        return;
      }
      onClick(e);
    },
    [onClick],
  );

  return (
    <div
      className={clsx(
        classes.filterContainer,
        prefixIcon && classes['filterContainer--with-prefix'],
        noPadding && classes['filterContainer--no-padding'],
        dropdown.arrowSuffix && classes['filterContainer--with-dropdown-arrow'],
        dropdown.isOpen && classes['filterContainer--with-dropdown'],
        className,
      )}
      ref={containerRef}
      onClick={onContainerClick}
    >
      {/* Icon */}
      {prefixIcon && (
        <Icon
          icon={prefixIcon}
          className={classes.filterContainer__prefixIcon}
        />
      )}

      {/* Inner */}
      {children}

      {/* Clear button */}
      {onClear && (
        <span
          className={clsx(classes.filterContainer__clearButton)}
          onClick={onClear}
        >
          <Icon icon={'clear'} />
        </span>
      )}

      {/* Icon */}
      {dropdown.arrowSuffix && (
        <Icon
          icon={dropdown.isOpen ? 'chevron-up' : 'chevron-down'}
          className={classes.filterContainer__dropdownArrow}
        />
      )}

      {/* Dropdown */}
      {dropdown.isOpen && (
        <div
          className={clsx(
            classes.filterContainer__dropdown,
            dropdown.fullWidth &&
              classes['filterContainer__dropdown--full-width'],
            !dropdown.allowOverflow && classes['overflowHidden'],
          )}
          ref={dropdownRef}
        >
          {dropdown.component}
        </div>
      )}

      {/* Notification icon */}
      {notification && (
        <div className={classes.filterContainer__notification} />
      )}
    </div>
  );
};
